import { PlayerApiEventName } from '@yandex-int/ims.stream-player-public-types/build-es5/PlayerApiEvents';
import { PlayerStartStage } from '@yandex-int/ims.stream-player-public-types/build-es5/PlayerStartStage';
import { PublicPlayerApi } from '@yandex-int/ims.stream-player-public-types/build-es5/PublicPlayerApi';
import { VideoPlayingState } from '@yandex-int/ims.stream-player-public-types/build-es5/VideoPlayingState';
import { PlayerListSdkApi } from '@yandex-int/ims.stream-player-public-types/build-es5/list-sdk/PlayerListSdkApi';
import { PlayerListSdkEvents } from '@yandex-int/ims.stream-player-public-types/build-es5/list-sdk/PlayerListSdkEvents';
import { PlayerListSdkItemsList } from '@yandex-int/ims.stream-player-public-types/build-es5/list-sdk/PlayerListSdkItemsList';
import { Constructor, Loop, OnProgessChange, VideoPlayerSlot, VideoPlayerState } from './types';

export class Player {
    private classInstance: Player | null = null;
    private listSdk: PlayerListSdkApi | null = null;
    private player: Omit<PublicPlayerApi<unknown>, 'setSource'> | null = null;
    private setPlayerEnabled: ((isPlayerEnabled: boolean) => void) | null = null;
    private slots: VideoPlayerSlot[] = [];
    private setExternalPlayerState: ((state: VideoPlayerState) => void) | null = null;
    private SHORT_VIDEO_DURATION_LIMIT = 4;
    // Плеер обновляет currentTime примерно раз в 100mc, поэтому берем 200,
    // чтобы гарантированно пропускать не больше 1 обновления
    private LOOP_TAIL = 0.2;
    private sessionPlayerState: { muted: VideoPlayerState['muted'] } = {
        muted: true,
    };
    private ObservablePlayerApiEvents: PlayerApiEventName[] = [
        PlayerApiEventName.StartStageChange,
        PlayerApiEventName.PlayingStateChange,
        PlayerApiEventName.SourceChange,
        PlayerApiEventName.VolumeChange,
        PlayerApiEventName.MutedChange,
    ];

    public activeSlot: VideoPlayerSlot | null = null;

    constructor({ setPlayerState, setPlayerEnabled }: Constructor) {
        if (this.classInstance) {
            return this.classInstance;
        }

        this.classInstance = this;
        this.setPlayerEnabled = setPlayerEnabled;
        this.setExternalPlayerState = setPlayerState;
        this.createInstance(this.slots);

        return this.classInstance;
    }

    private createInstance = (config: PlayerListSdkItemsList) => {
        this.listSdk = (window as any).Ya.playerListSdk.init(
            config,
            {
                loop: false,
                additionalParams: {
                    from: 'rythm',
                },
            },
            {
                loadSkin: async () => null,
                objectFit: 'cover',
                autoplay: true,
                muted: true,
            },
        );

        this.onSdkInit();
    };

    private onSdkInit = async () => {
        const sdk = this.listSdk;

        if (!sdk) {
            return;
        }

        sdk.once(PlayerListSdkEvents.Init, () => {
            this.player = sdk.getPlayer();
            this.setInitialState();
            this.handleSubscribes();
            this.setPlayerEnabled?.(true);
        });
    };

    // Задает плеерные значения актуальные для всей сессии
    private setInitialState = () => {
        this.setMuted(this.sessionPlayerState.muted);
    };

    private handleSubscribes = async () => {
        if (!this.player) {
            return;
        }

        const player = this.player;

        player.on(PlayerApiEventName.MutedChange, (e) => {
            this.activeSlot?.onToggleMuteUnmute?.(e.muted);
        });

        player.on(PlayerApiEventName.CurrentTimeChange, this.loop);

        this.ObservablePlayerApiEvents.forEach((eventName) =>
            player.on(eventName, () => {
                const currentState = player.getState();

                this.setExternalPlayerState?.({
                    playingState: currentState?.playingState || VideoPlayingState.BUFFERING,
                    startStage: currentState?.startStage || PlayerStartStage.init,
                    muted: currentState?.muted ?? true,
                    isShortVideo: currentState?.duration <= this.SHORT_VIDEO_DURATION_LIMIT,
                });

                this.sessionPlayerState = {
                    muted: currentState?.muted ?? true,
                };
            }),
        );
    };

    // Делаем loop через событие CurrentTimeChange, чтобы избежать столдов
    // и бага с мерцанием, при подмене видео на превью в момент END
    private loop: Loop = ({ currentTime }) => {
        const duration = this.player?.getState()?.duration;

        if (duration && duration - currentTime < this.LOOP_TAIL) {
            this.activeSlot?.onVideoEnd?.();
            this.player?.seek(0);
            this.activeSlot?.onVideoStart?.();
        }
    };

    public mount = (slot: VideoPlayerSlot) => {
        this.slots.push(slot);

        this.listSdk ? this.listSdk.updateItemsList(this.slots) : this.createInstance(this.slots);

        this.listSdk?.updateItemsList(this.slots);

        return () => {
            this.slots = this.slots.filter((node) => node.container !== slot.container);

            // Если колиество активных слотов равно нулю, и плеер уже заиничен, то дестроим SDK,
            // так как оно не может жить с пустыми слотами
            if (!this.slots.length && this.player) {
                this.listSdk?.destroy();
                this.setPlayerEnabled?.(false);
                this.listSdk = null;
                this.player = null;

                return;
            }

            this.listSdk?.updateItemsList(this.slots);
        };
    };

    public setActiveItem = (node: HTMLElement) => {
        if (node === this.activeSlot?.container) {
            return;
        }

        this.listSdk?.setActiveItem(node);

        const activeSlot = this.slots.find((slot) => slot.container === node);

        this.activeSlot = activeSlot || null;
        this.player?.play();
        this.activeSlot?.onVideoStart?.();
    };

    public pause = (node: HTMLElement) => {
        if (this.activeSlot?.container !== node) {
            return;
        }

        this.player?.pause();
        this.activeSlot?.onVideoPause?.();
    };

    public play = (node: HTMLElement) => {
        if (this.activeSlot?.container === node) {
            this.player?.play();
            this.activeSlot?.onVideoPlay?.();
        } else {
            this.setActiveItem(node);
        }
    };

    public setMuted = (muted: boolean) => {
        this.player?.setMuted(muted);
    };

    public onProgessChange: OnProgessChange = (callback) => {
        const innerCb = ({ currentTime }: { currentTime: number }) => {
            const duration = this.player?.getState()?.duration;
            const progress = currentTime && duration ? (currentTime / duration) * 100 : 0;

            callback(progress);
        };

        this.player?.on(PlayerApiEventName.CurrentTimeChange, innerCb);

        return () => {
            this.player?.off(PlayerApiEventName.CurrentTimeChange, innerCb);
        };
    };
}
